/**
 * Super-admin feature flags for the ops portal.
 * Stored client-side and applied to admin UI behavior.
 */
export type AdminFeatureFlagKey =
  | 'hideAdminViewEvents'
  | 'addInternalNoteEnabled'
  | 'exceptionVehicleClassingEnabled'
  | 'analyticsEnabled'
  | 'salesforceCasesNavEnabled';

export interface AdminFeatureFlagDefinition {
  readonly key: AdminFeatureFlagKey;
  readonly label: string;
  readonly description: string;
  readonly defaultEnabled: boolean;
}

export const ADMIN_FEATURE_FLAG_DEFINITIONS: ReadonlyArray<AdminFeatureFlagDefinition> = [
  {
    key: 'hideAdminViewEvents',
    label: 'Hide ADMIN_VIEW events in System Logs',
    description:
      'When enabled, ADMIN_VIEW audit events are excluded from the System Logs table and counts.',
    defaultEnabled: false,
  },
  {
    key: 'addInternalNoteEnabled',
    label: 'Add Internal Note',
    description:
      'When enabled, admins can add internal notes on request detail pages. Notes appear in the audit timeline.',
    defaultEnabled: false,
  },
  {
    key: 'exceptionVehicleClassingEnabled',
    label: 'Exception vehicle classing',
    description:
      'When enabled, shows the vehicle classing table on exception review with Phoenix class data.',
    defaultEnabled: true,
  },
  {
    key: 'analyticsEnabled',
    label: 'Analytics',
    description:
      'When enabled, shows the Analytics page in the sidebar for KPI charts and denial-reason breakdown.',
    defaultEnabled: false,
  },
  {
    key: 'salesforceCasesNavEnabled',
    label: 'Salesforce Cases nav',
    description:
      'When enabled, shows Salesforce Cases in the sidebar. Exception detail still shows the Salesforce case link when this is off.',
    defaultEnabled: true,
  },
];

export type AdminFeatureFlagState = Record<AdminFeatureFlagKey, boolean>;

export function defaultAdminFeatureFlagState(): AdminFeatureFlagState {
  return ADMIN_FEATURE_FLAG_DEFINITIONS.reduce((acc, def) => {
    acc[def.key] = def.defaultEnabled;
    return acc;
  }, {} as AdminFeatureFlagState);
}
